<template>
  <focus-trap :clickDeactivates="false" :escapeDeactivates="false">
    <div
      class="measurement-result-popup"
      :tabindex="`-${layerNumber}`"
      @keyup.esc.stop="handleClose"
    >
      <popup
        :buttons="popupButtons"
        :content-style="{
          maxHeight: '650px',
          marginTop: '25px',
          padding: '0 30px'
        }"
        :disabled-flg="isUpdating"
        :layer-number="layerNumber"
        @cancel="handleClose"
        @click-close-mark="handleClose"
        @close="handleClose"
        @decision="unlinkPatient"
      >
        <template v-slot:content>
          <div class="content">
            <div class="header">
              <div class="title">検査結果</div>
              <div class="buttons">
                <base-button-small-white
                  v-if="!lookOnlyFlg && canUpdate"
                  data-test="base-button-small-white edit"
                  :disabled="isProcessing"
                  @click="openEditPopup"
                  >編集</base-button-small-white
                >
                <base-button-small-red
                  v-if="!lookOnlyFlg && canDelete"
                  data-test="base-button-small-red delete"
                  :disabled="isProcessing"
                  @click="openDeletePopup"
                  >削除</base-button-small-red
                >
              </div>
            </div>
            <div class="detail">
              <div class="patient">
                <measurement-result-patient-detail
                  data-test="measurement-result-patient-detail"
                  :patient="patient"
                  :run-date="measurementResult.date"
                />
              </div>
              <div class="measurement-result">
                <div class="measurement-result-table">
                  <div class="table-row">
                    <div class="table-cell label">検査日時</div>
                    <div class="table-cell parentheses">
                      <span>{{ jaRunDateTime }}</span>
                    </div>
                  </div>
                  <div class="table-row">
                    <div class="table-cell label">検査名</div>
                    <div class="table-cell">{{ categoryName }}</div>
                  </div>
                  <div class="table-row">
                    <div class="table-cell label">検査機器</div>
                    <div class="table-cell instrument">
                      <span>{{ instrumentName }}</span>
                    </div>
                  </div>
                  <div class="table-row">
                    <div class="table-cell label">動物種</div>
                    <div class="table-cell">{{ labSpeciesName }}</div>
                  </div>
                  <div
                    v-if="isNxMeasurementResult"
                    class="table-row"
                    data-test="specimen-id"
                  >
                    <div class="table-cell label">No.</div>
                    <div class="table-cell">{{ specimenId }}</div>
                  </div>
                  <div
                    v-if="isNxMeasurementResult"
                    class="table-row"
                    data-test="nx-patient-id"
                  >
                    <div class="table-cell label">検体ID</div>
                    <div class="table-cell">{{ nxPatientId }}</div>
                  </div>
                </div>
                <div class="measurement-result-items-table">
                  <div class="table-header">
                    <div class="table-cell label left">検査項目</div>
                    <div class="table-cell label">結果</div>
                    <div class="table-cell label">単位</div>
                    <div class="table-cell label">基準値</div>
                  </div>
                  <div class="table-body">
                    <div
                      v-for="item in displayMeasurementResultItems"
                      class="table-body-row"
                      data-test="table-body-row"
                      :key="item.id"
                    >
                      <div class="table-cell left">{{ item.name }}</div>
                      <div class="table-cell">{{ item.result }}</div>
                      <div class="table-cell">{{ item.unit }}</div>
                      <div class="table-cell">{{ item.range }}</div>
                    </div>
                  </div>
                </div>
                <div v-if="showNote" class="note" data-test="note">
                  <div class="label">注釈：</div>
                  <div class="value">{{ measurementResult.note }}</div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </popup>
      <announce-popup
        v-if="deletePopup.isOpened"
        data-test="delete-popup"
        :buttons="deletePopup.buttons"
        :disabled="isDeleting"
        :layer-number="deletePopup.layerNumber"
        :title="deletePopup.title"
        :type="deletePopup.type"
        @cancel="closeDeletePopup"
        @decision="decideDelete"
        >{{ deletePopup.message }}</announce-popup
      >
      <announce-popup
        v-if="resultPopup.isOpened"
        data-test="result-popup"
        :buttons="resultPopup.buttons"
        :layer-number="resultPopup.layerNumber"
        :title="resultPopup.title"
        :type="resultPopup.type"
        @close="closeResultPopup"
        >{{ resultPopup.message }}</announce-popup
      >
      <measurement-result-edit-popup
        v-if="showEditPopup"
        data-test="measurement-result-edit-popup"
        :layer-number="layerNumber + 1"
        :measurement-result-id="measurementResultId"
        :patient-id="patientId"
        @close="closeEditPopup"
        @close-parent="() => $emit('close')"
      />
    </div>
  </focus-trap>
</template>

<script>
import AnnouncePopup from '@/components/popups/AnnouncePopup'
import BaseButtonSmallRed from '@/components/parts/atoms/BaseButtonSmallRed'
import BaseButtonSmallWhite from '@/components/parts/atoms/BaseButtonSmallWhite'
import MeasurementResultEditPopup from '@/components/popups/MeasurementResultEditPopup'
import MeasurementResultPatientDetail from '@/components/parts/molecules/MeasurementResultPatientDetail'
import Popup from '@/components/popups/Popup'
import dedent from 'dedent'
import { FocusTrap } from 'focus-trap-vue'
import {
  isNxMeasurementResult,
  toDisplayItemName,
  toDisplayRange,
  toDisplayValue
} from '@/utils/measurement_utils'
import { mapGetters } from 'vuex'
import { toJaDateTime } from '@/utils/moment'

export default {
  name: 'MeasurementResultPopup',

  components: {
    AnnouncePopup,
    BaseButtonSmallRed,
    BaseButtonSmallWhite,
    FocusTrap,
    MeasurementResultEditPopup,
    MeasurementResultPatientDetail,
    Popup
  },

  props: {
    buttons: { type: Array, default: () => ['閉じる'] },
    canDelete: { type: Boolean, default: false },
    canUpdate: { type: Boolean, default: false },
    layerNumber: { type: Number, default: 1 },
    measurementResultId: { type: Number, default: 0 },
    patientId: { type: Number, default: 0 }
  },

  data() {
    return {
      deletePopup: {
        buttons: ['削除しない', '削除する'],
        isOpened: false,
        layerNumber: this.layerNumber + 1,
        message: '削除してもよろしいですか？',
        title: '確認',
        type: 'alert'
      },
      idexxInstrumentCodes: ['003', '015', '017', '014'],
      isDeleting: false,
      isUpdating: false,
      resultPopup: {
        buttons: ['閉じる'],
        isOpened: false,
        layerNumber: this.layerNumber + 2,
        message: '',
        title: '',
        type: ''
      },
      shouldCloseSelf: false,
      showEditPopup: false
    }
  },

  computed: {
    ...mapGetters({
      getCategory: 'master/getMasterLaboratoryCategoryIndexedByCode',
      getInstrument: 'master/getMasterLaboratoryInstrumentIndexedByCode',
      getItem: 'master/getMasterLaboratoryItemIndexedByCode',
      getLabSpecies: 'master/getMasterLaboratorySpeciesIndexedByCode',
      getMeasurementResult: 'measurementResults/getDataById',
      getMeasurementResultItems:
        'measurementResultItems/getDataByMeasurementResultId',
      getNxMeasurementResult:
        'nxMeasurementResults/getDatumByMeasurementResultId',
      getPatient: 'patients/getDataById',
      lookOnlyFlg: 'auth/lookOnlyFlg'
    }),
    categoryName() {
      const categoryCode = this.measurementResult.masterLaboratoryCategoryCode
      const category = this.getCategory(categoryCode)
      return category?.name ?? ''
    },
    displayMeasurementResultItems() {
      return this.measurementResultItems.map(v => {
        const instrumentCode = this.measurementResult
          .masterLaboratoryInstrumentCode
        return {
          id: v.id,
          name: this.toItemName(v),
          result: toDisplayValue(instrumentCode, v),
          unit: v.unit,
          range: this.toRange(v)
        }
      })
    },
    instrumentName() {
      const instrumentCode = this.measurementResult
        .masterLaboratoryInstrumentCode
      const instrument = this.getInstrument(instrumentCode)
      return instrument?.name ?? ''
    },
    isNxMeasurementResult() {
      return isNxMeasurementResult(this.measurementResult)
    },
    isProcessing() {
      return this.isDeleting || this.isUpdating
    },
    jaRunDateTime() {
      const { date, time } = this.measurementResult
      const YYYYMMDDHHmm = date && time ? `${date}${time}` : ''
      return toJaDateTime(YYYYMMDDHHmm)
    },
    labSpeciesName() {
      const speciesCode = this.measurementResult.masterLaboratorySpeciesCode
      const labSpecies = this.getLabSpecies(speciesCode)
      return labSpecies?.name ?? ''
    },
    measurementResult() {
      const measurementResult = this.getMeasurementResult(
        this.measurementResultId
      )
      return measurementResult ?? {}
    },
    measurementResultItems() {
      const measurementResultItems = this.getMeasurementResultItems(
        this.measurementResultId
      )
      return measurementResultItems ?? []
    },
    nxMeasurementResult() {
      const nxMeasurementResult = this.getNxMeasurementResult(
        this.measurementResultId
      )
      return nxMeasurementResult ?? {}
    },
    nxPatientId() {
      return this.nxMeasurementResult?.nxPatientId ?? ''
    },
    patient() {
      return this.getPatient(this.patientId)
    },
    popupButtons() {
      return this.lookOnlyFlg ? ['閉じる'] : this.buttons
    },
    showNote() {
      return this.idexxInstrumentCodes.includes(
        this.measurementResult.masterLaboratoryInstrumentCode
      )
    },
    specimenId() {
      return this.nxMeasurementResult?.specimenId ?? ''
    }
  },

  methods: {
    closeDeletePopup() {
      this.deletePopup.isOpened = false
    },
    closeEditPopup() {
      this.showEditPopup = false
    },
    closeResultPopup() {
      this.resultPopup.isOpened = false
      if (this.shouldCloseSelf) {
        this.$emit('close')
      }
    },
    async decideDelete() {
      this.isDeleting = true
      const result = await this.$store.dispatch(
        'measurementResults/delete',
        this.measurementResultId
      )
      if (result === 'success') {
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message: '削除しました',
          title: '完了',
          type: 'success'
        }
        this.shouldCloseSelf = true
      } else {
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message: '削除に失敗しました',
          title: '失敗',
          type: 'failure'
        }
        if (result === 'no data in clinic') {
          this.shouldCloseSelf = true
        }
      }
      this.isDeleting = false
    },
    handleClose() {
      this.$emit('close')
    },
    openDeletePopup() {
      this.deletePopup.isOpened = true
    },
    openEditPopup() {
      this.showEditPopup = true
    },
    async unlinkPatient() {
      this.isUpdating = true
      const result = await this.$store.dispatch('measurementResults/unlink', {
        id: this.measurementResultId,
        patientId: this.patientId
      })
      if (result === 'success') {
        this.$emit('close')
      } else {
        let message = '取込解除に失敗しました'
        if (result === 'no patient') {
          message = dedent`
            取込解除に失敗しました。
            対象の患者は既に削除されています。
          `
          this.shouldCloseSelf = true
        } else if (
          result === 'no data in clinic' ||
          result === 'no measurementResult'
        ) {
          message = dedent`
            取込解除に失敗しました。
            対象の検査結果は既に削除されています。
          `
          this.shouldCloseSelf = true
        }
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message,
          title: '失敗',
          type: 'failure'
        }
      }
      this.isUpdating = false
    },
    toItemName(measurementResultItem) {
      const itemCode = measurementResultItem.masterLaboratoryItemCode
      const masterLaboratoryItem = this.getItem(itemCode)
      return toDisplayItemName(
        masterLaboratoryItem?.enName,
        measurementResultItem
      )
    },
    toRange(measurementResultItem) {
      const { referenceRangeLow, referenceRangeHigh } = measurementResultItem
      return toDisplayRange(referenceRangeLow, referenceRangeHigh)
    }
  }
}
</script>

<style lang="scss" scoped>
$header-height: 43px;
$patient-detail-height: 70px;
$table-row-height: 30px;

@mixin table-cell {
  box-sizing: border-box;
  align-content: center;
  padding: 4px;
  border-right: 1px solid #{$light-grey};
  border-bottom: 1px solid #{$light-grey};
  font-size: 13px;
  overflow-wrap: anywhere;
  line-break: strict;
  &.label {
    background-color: #{$very-light-shade-orange};
  }
  &.left {
    text-align: left;
  }
}

.measurement-result-popup {
  position: absolute;
  top: 0;
  left: 0;
  .content {
    width: 700px;
    > .header {
      position: sticky;
      top: 0;
      z-index: 200;
      background: #fff;
      display: flex;
      justify-content: space-between;
      height: $header-height;
      > .title {
        text-align: left;
        font-size: 16px;
        font-weight: bold;
        color: #{$greyish-brown};
      }
      > .buttons {
        display: flex;
        gap: 0 10px;
      }
    }
    > .detail {
      > .patient {
        position: sticky;
        top: $header-height;
        z-index: 100;
        background: #fff;
      }
      > .measurement-result {
        min-height: 260px;
        > .measurement-result-table {
          box-sizing: border-box;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          border-top: 1px solid #{$light-grey};
          border-left: 1px solid #{$light-grey};
          > .table-row {
            box-sizing: border-box;
            grid-column: span 1;
            display: grid;
            grid-template-columns: 70px auto;
            height: $table-row-height;
            > .table-cell {
              @include table-cell;
              text-align: left;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              &.parentheses {
                font-feature-settings: 'palt';
              }
              &.instrument {
                font-size: 12px;
              }
            }
          }
        }
        > .measurement-result-items-table {
          box-sizing: border-box;
          display: grid;
          grid-template-columns: repeat(4, auto);
          border-left: 1px solid #{$light-grey};
          > .table-header {
            box-sizing: border-box;
            display: grid;
            grid-template-columns: subgrid;
            grid-column: span 4;
            height: $table-row-height;
            > .table-cell {
              @include table-cell;
              text-align: center;
            }
          }
          > .table-body {
            box-sizing: border-box;
            display: grid;
            grid-template-columns: subgrid;
            grid-column: span 4;
            > .table-body-row {
              box-sizing: border-box;
              display: grid;
              grid-template-columns: subgrid;
              grid-column: span 4;
              min-height: $table-row-height;
              &:nth-child(even) {
                background-color: #f7f7f7;
              }
              > .table-cell {
                @include table-cell;
                text-align: center;
              }
            }
          }
        }
        > .note {
          min-height: 50px;
          white-space: pre-wrap;
          border-left: 1px solid #{$light-grey};
          border-right: 1px solid #{$light-grey};
          border-bottom: 1px solid #{$light-grey};
          padding: 8px;
          > .label {
            font-size: 13px;
            font-weight: bold;
          }
          > .value {
            font-size: 13px;
          }
        }
      }
    }
  }
}
</style>
